import { useEffect, useState } from 'react';
import type { Group } from '@/types/group';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateGroupName } from '@/hooks/admin/group/use-update-group-name';

interface EditGroupNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: Group | null;
  onGroupUpdated?: () => void;
}

export function EditGroupNameDialog({
  open,
  onOpenChange,
  group,
  onGroupUpdated,
}: EditGroupNameDialogProps) {
  const [groupName, setGroupName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { updateGroupName, isLoading } = useUpdateGroupName();

  useEffect(() => {
    if (!open || !group) {
      setErrorMessage(null);
      return;
    }

    setGroupName(group.name);
    setErrorMessage(null);
  }, [group, open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setErrorMessage(null);
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async () => {
    if (!group) return;

    setErrorMessage(null);

    try {
      await updateGroupName({
        groupId: group.id,
        groupName: groupName.trim(),
      });
      onGroupUpdated?.();
      handleOpenChange(false);
    } catch {
      setErrorMessage('Failed to update group name.');
    }
  };

  if (!group) return null;

  const trimmedName = groupName.trim();
  const isSaveDisabled =
    trimmedName.length === 0 || trimmedName === group.name || isLoading;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit group name</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <Label htmlFor="group-name" className="sr-only">
            Group name
          </Label>
          <Input
            id="group-name"
            value={groupName}
            onChange={(e) => {
              setGroupName(e.target.value);
              setErrorMessage(null);
            }}
            placeholder="Enter group name"
            autoFocus
          />
          {errorMessage ? (
            <div className="text-sm text-rose-600">{errorMessage}</div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaveDisabled}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
